import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
  inject,
} from '@angular/core';
import { Observable } from 'rxjs';
import { combineLatestWith, filter } from 'rxjs/operators';
import { DayOrWeekCalendarModel } from '../../../../core/models/calendar/day-or-week-calendar.model';
import { SidebarService } from '../../../../core/services/ui/sidebar.service';
import { CalendarEvents, CalendarViews } from '../../calendar-types';
import { CalendarHeaderService } from '../../components/calendar-header/calendar-header.service';
import { TimeBlockDurationCalculationService } from '../../components/time-block/calculation/duration/time-block-duration-calculation.service';
import { ParallelTimeBlockGeometryCalculatorService } from '../../components/time-block/calculation/geometry/parallel-time-block-geometry-calculator.service';
import { TimeBlockCrudService } from '../../components/time-block/crud/time-block-crud.service';
import { TimeBlockHookContainer } from '../../components/time-block/types/time-block-types';
import { CalendarDayOrWeekViewService } from '../../services/calendar-day-or-week-view.service';
import { CalendarScrollbarService } from '../../services/calendar-scrollbar.service';
import { CalendarViewHandlerService } from '../../services/calendar-view-handler.service';
import { CalendarService } from '../../services/calendar.service';
import { TimeCoordinateMappingService } from '../../time-mapping/time-coordinate-mapping.service';
import { WINDOW } from '../../util/browser-window-ref';
import { CalendarBodyCSSId } from '../../util/calendar-style.constants';
import { DateTimeHelper } from '../../util/date-time-helper';
import { DayOrWeekViewHandler } from '../handlers/day-or-week-view-handler';
import { TimeBlockContainerDirective } from '../time-block-container.directive';
import { TimeBlockInjectable } from '../time-block-injectable';
import { Viewable } from '../viewable';

@Component({
  selector: 'app-week-view',
  templateUrl: './week-view.component.html',
  styleUrls: ['./week-view.component.scss'],
  standalone: false,
})
export class WeekViewComponent
  extends DayOrWeekViewHandler
  implements OnInit, AfterViewInit, OnDestroy, TimeBlockInjectable, Viewable
{
  // calendar time grid lanes
  @ViewChildren(TimeBlockContainerDirective)
  public timeLanePlaceholders!: QueryList<TimeBlockContainerDirective>;
  @ViewChildren('timeBlockLanes', { read: ElementRef })
  public timeBlockLanesContainer: QueryList<ElementRef>;
  // calendar body
  @ViewChild('htmlItemContainer') public htmlItemContainer: ElementRef<HTMLElement>;
  // calendar full day lanes from calendar header
  @Input() fixIntervalLanesLoaded$: Observable<
    [QueryList<TimeBlockContainerDirective>, QueryList<ElementRef>]
  >;
  public readonly CalendarView = CalendarViews;
  private fixIntervalLanePlaceholders!: QueryList<TimeBlockContainerDirective>;
  private fixIntervalLaneHtmlItemContainer: QueryList<ElementRef>;

  // DI
  private readonly window = inject<Window>(WINDOW);

  constructor(
    public calendarService: CalendarService,
    public calendarDayOrWeekViewService: CalendarDayOrWeekViewService,
    private readonly cd: ChangeDetectorRef,
    private readonly timeBlockCrudService: TimeBlockCrudService,
    private readonly calendarScrollbarService: CalendarScrollbarService,
    private readonly parallelTimeBlockGeometryCalculatorService: ParallelTimeBlockGeometryCalculatorService,
    private readonly timeBlockDurationCalculationService: TimeBlockDurationCalculationService,
    private readonly calendarHeaderService: CalendarHeaderService,
    private readonly timeCoordinateMappingService: TimeCoordinateMappingService,
    private readonly sidebarService: SidebarService,
    private readonly calendarViewHandlerService: CalendarViewHandlerService,
  ) {
    super(
      calendarService,
      timeBlockCrudService,
      timeCoordinateMappingService,
      sidebarService,
      calendarViewHandlerService,
    );
  }

  ngOnInit(): void {
    this.calendarScrollbarService.initializeScrollbars();
    this.calendarDayOrWeekViewService.calcBusinessTimeAreas();
  }

  ngAfterViewInit(): void {
    this.cd.detach();
    this.window.setTimeout(() => {
      this.initCalendarView();
    });

    let model = new DayOrWeekCalendarModel();
    Object.assign(model, this.calendarService.model);

    this.calendarService.emitCalendarChange(model, CalendarEvents.InitializedCalendarView);

    this.subs.sink = this.timeBlockCrudService.timeBlocksFetched$
      .pipe(
        combineLatestWith(this.fixIntervalLanesLoaded$),
        filter(
          ([timeBlocks, [placeHolders, containers]]) =>
            timeBlocks !== null && placeHolders !== null && containers !== null,
        ),
      )
      .subscribe(
        ([[fixIntervalTimeBlockMap, variableIntervalTimeBlockMap], [placeHolders, containers]]) => {
          this.cd.detectChanges();
          this.fixIntervalLanePlaceholders = placeHolders;
          this.fixIntervalLaneHtmlItemContainer = containers;
          this.setModelTemplateData();
          // Calculate height of full time block container
          this.updateAndRenderFixIntervalContainer();

          // Insert the time blocks into the calendar model
          model = this.calendarService.model as DayOrWeekCalendarModel;
          this.insertTimeBlocksIntoView(
            fixIntervalTimeBlockMap,
            model.fixIntervalCalendarModel.timeBlocks,
            variableIntervalTimeBlockMap,
            model.timeBlocks,
          );

          // Calculate geometry for parallel full day time blocks
          this.parallelTimeBlockGeometryCalculatorService.updateParallelTimeBlockGeometries(true);
          // Calculate geometry for parallel inner day time blocks
          this.parallelTimeBlockGeometryCalculatorService.updateParallelTimeBlockGeometries(false);

          // Render fix lane container with correct height
          this.calendarHeaderService.initDayOrWeekViewFixLaneDayContainer();
          this.calendarHeaderService.renderDayOrWeekViewFixLaneDayContainer();
          this.calendarHeaderService.toggleDayOrWeekViewFixLaneDayContainer(false);

          // After fix lane day calendar was populated, re-calculate top offset since the height of the fix lane calendar probably changed.
          const bcr = this.htmlItemContainer.nativeElement.getBoundingClientRect();

          this.calService.model.geometryData.calendarBodyOffsetTop =
            bcr.top + this.calendarScrollbarService.calendarBodyScrollbar.scrollData.scrollPosY;
          this.calService.model.geometryData.calendarBodyVisibleHeight =
            document.querySelector(CalendarBodyCSSId).clientHeight;
          this.calendarService.emitCalendarChange(model, CalendarEvents.RenderedTimeBlocks);
          this.timeBlockDurationCalculationService.calculateTimeBlockDurationSums$.next(null);
        },
      );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.destroy();
  }

  public isWorkTimeBoundary(hourLabel: string | undefined): boolean {
    if (!hourLabel) {
      return false;
    }

    const referenceDate = new Date();
    const model = this.calendarService.model as DayOrWeekCalendarModel;

    const hour = DateTimeHelper.parse(
      hourLabel,
      model.calendarProperties.timeFormat,
      referenceDate,
    );

    const workTimeStart = model.calendarProperties.workTimeStart;
    const workTimeEnd = model.calendarProperties.workTimeEnd;
    return (
      DateTimeHelper.isSameTime(hour, workTimeStart) || DateTimeHelper.isSameTime(hour, workTimeEnd)
    );
  }

  protected setModelTemplateData(): void {
    const updatedWeekCalendarModel = this.calendarService.model as DayOrWeekCalendarModel;
    updatedWeekCalendarModel.timeBlockContainers = this.timeLanePlaceholders.map((e) => {
      e.viewContainerRef.clear();
      const hookContainer: TimeBlockHookContainer = {
        vcr: e.viewContainerRef,
        cd: e.changeDetectorRef,
      };
      return hookContainer;
    });
    updatedWeekCalendarModel.timeBlockHtmlContainers = this.timeBlockLanesContainer.toArray();

    // init full day model
    const fixIntervalDayOrWeekCalendarModel = updatedWeekCalendarModel.fixIntervalCalendarModel;
    fixIntervalDayOrWeekCalendarModel.timeBlockContainers = this.fixIntervalLanePlaceholders.map(
      (e) => {
        e.viewContainerRef.clear();
        const hookContainer: TimeBlockHookContainer = {
          vcr: e.viewContainerRef,
          cd: e.changeDetectorRef,
        };
        return hookContainer;
      },
    );

    fixIntervalDayOrWeekCalendarModel.timeBlockHtmlContainers =
      this.fixIntervalLaneHtmlItemContainer.toArray();

    this.calendarService.emitCalendarChange(updatedWeekCalendarModel, []);
  }

  protected initCalendarView(): void {
    if (!this.initialized || this.viewportSizeChanged) {
      if (!this.viewportSizeChanged) {
        this.mapHoursOfDay();
        this.cd.detectChanges();
      }

      this.updateCalendarDimensions(
        this.htmlItemContainer,
        this.calendarScrollbarService.calendarBodyScrollbar.scrollData.scrollPosY,
      );
    }
  }
}
